import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { normalizeTenantPlan } from '../../utils/planAccess';
import UpgradeModal from '../../components/UpgradeModal';
import { useFoodDeliveryInfo } from '../../utils/foodDelivery';

const Rooms = () => {
    const { user } = useAuthStore();
    const [rooms, setRooms] = useState([]);
    const [dragId, setDragId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editRoom, setEditRoom] = useState(null);
    const [form, setForm] = useState({ name: '', sort_order: 0, count_occupancy: true, hidden_waiter_ids: [] });
    const [waiters, setWaiters] = useState([]); // ofisiant siyahısı (zal giriş icazəsi üçün)
    const [restrictWaiters, setRestrictWaiters] = useState(false); // ofisiant siyahısı açıqdır? (default: hamısı görür)
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    // Superadmin «Food delivery → otaqlara bağla» açıqdırsa otaq «Yalnız Food delivery» edilə bilər
    const fdRooms = useFoodDeliveryInfo().rooms;

    const tenantPlan = normalizeTenantPlan(user?.tenant_plan);
    const canManageRooms = tenantPlan === 'pro';

    useEffect(() => {
        if (user?.role !== 'superadmin') { loadRooms(); loadWaiters(); }
        else setLoading(false);
    }, [user]);

    const loadWaiters = async () => {
        try {
            const res = await api.get('/auth/waiters');
            setWaiters((res.data?.data || []).filter(w => Number(w.active) !== 0));
        } catch { /* ofisiant siyahısı alınmadı */ }
    };

    const loadRooms = async () => {
        try {
            const res = await api.get('/rooms/all');
            setRooms(res.data.data);
        } catch {
            toast.error('Otaqlar yüklənmədi.');
        } finally {
            setLoading(false);
        }
    };

    // Drag & drop reorder
    const handleDragStart = (id) => setDragId(id);
    const handleDragOver  = (e) => e.preventDefault();
    const handleDrop = async (targetId) => {
        if (!dragId || dragId === targetId) { setDragId(null); return; }
        const oldIdx = rooms.findIndex(r => r.id === dragId);
        const newIdx = rooms.findIndex(r => r.id === targetId);
        if (oldIdx < 0 || newIdx < 0) { setDragId(null); return; }
        const reordered = [...rooms];
        const [moved] = reordered.splice(oldIdx, 1);
        reordered.splice(newIdx, 0, moved);
        setRooms(reordered);
        setDragId(null);
        try {
            await api.post('/rooms/reorder', { ids: reordered.map(r => r.id) });
            toast.success('Zal sırası yeniləndi.');
        } catch {
            toast.error('Sıra yenilənə bilmədi.');
            loadRooms();
        }
    };

    const requestUpgrade = () => {
        setShowUpgradeModal(true);
    };

    const openCreate = () => {
        if (!canManageRooms) {
            requestUpgrade();
            return;
        }

        setForm({ name: '', sort_order: rooms.length + 1, count_occupancy: true, hidden_waiter_ids: [] });
        setRestrictWaiters(false);
        setEditRoom(null);
        setShowModal(true);
    };

    const openEdit = (room) => {
        if (!canManageRooms) {
            requestUpgrade();
            return;
        }

        setForm({
            name: room.name,
            sort_order: room.sort_order,
            count_occupancy: room.count_occupancy == null ? true : Number(room.count_occupancy) === 1,
            hidden_waiter_ids: Array.isArray(room.hidden_waiter_ids) ? room.hidden_waiter_ids.map(Number) : [],
            delivery_only: Number(room.delivery_only || 0) === 1,
        });
        setRestrictWaiters(Array.isArray(room.hidden_waiter_ids) && room.hidden_waiter_ids.length > 0);
        setEditRoom(room);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim()) {
            toast.error('Otaq adı yazın.');
            return;
        }

        try {
            if (editRoom) {
                await api.put(`/rooms/${editRoom.id}`, { ...form, active: editRoom.active });
                toast.success('Otaq yeniləndi.');
            } else {
                await api.post('/rooms', form);
                toast.success('Otaq əlavə edildi.');
            }

            setShowModal(false);
            loadRooms();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleToggle = async (room) => {
        try {
            if (!canManageRooms) {
                requestUpgrade();
                return;
            }

            await api.put(`/rooms/${room.id}`, {
                name: room.name,
                sort_order: room.sort_order,
                active: room.active ? 0 : 1
            });
            toast.success(room.active ? 'Deaktiv edildi.' : 'Aktiv edildi.');
            loadRooms();
        } catch {
            toast.error('Xəta baş verdi.');
        }
    };

    const handleDelete = async () => {
        if (!deleteCandidate) return;

        try {
            if (!canManageRooms) {
                requestUpgrade();
                return;
            }

            await api.delete(`/rooms/${deleteCandidate.id}`);
            toast.success('Otaq silindi.');
            setDeleteCandidate(null);
            loadRooms();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinə bilmədi.');
        }
    };

    const ICONS = ['🏠', '🍽️', '⭐', '🌿', '🎯', '🏛️', '🎪', '☕'];

    if (user?.role === 'superadmin') {
        return (
            <Layout>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
                    <div className="mb-3 text-4xl">⚠️</div>
                    <p className="font-semibold text-amber-800">Zallar yalnız restoran adminləri tərəfindən idarə olunur.</p>
                    <p className="mt-1 text-sm text-amber-600">Restoranı seçib onun admin panelinə daxil olun.</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <UpgradeModal
                open={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title="Otaqlar yalnız Pro paketdədir"
                message="Zal, kabinet və otaq qruplaşdırmasını açmaq üçün Pro plana keçin."
                plans={['Pro']}
            />

            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'serif' }}>
                        Zal / Otaqlar
                    </h1>
                    <p className="mt-1 text-sm text-gray-500">{rooms.filter((room) => room.active).length} aktiv otaq</p>
                </div>
                <button
                    onClick={openCreate}
                    className="rounded-xl px-4 py-2 text-sm font-bold text-white transition-all hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                >
                    + Otaq əlavə et
                </button>
            </div>

            <div className="mb-6 flex items-start gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
                <span className="mt-0.5 text-base">ℹ️</span>
                <span>
                    Otaqlar yaratdıqdan sonra <b>Masalar</b> səhifəsindən hər masaya otaq təyin edə bilərsiniz. Ofisiant masaları otaq tabları ilə görəcək.
                </span>
            </div>

            {!canManageRooms && (
                <div className="mb-6 flex items-center justify-between rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
                    <div>
                        <div className="text-sm font-bold text-amber-800">Otaq idarəsi Pro paket üçündür</div>
                        <div className="mt-1 text-xs text-amber-700">Sadə paketdə düymə görünür, amma otaq əlavə etmək üçün Pro plana keçmək lazımdır.</div>
                    </div>
                    <button
                        onClick={requestUpgrade}
                        className="rounded-xl border border-amber-300 bg-white px-4 py-2 text-xs font-bold text-amber-700 hover:bg-amber-100"
                    >
                        Pro-ya keç
                    </button>
                </div>
            )}

            {loading ? (
                <div className="flex h-48 items-center justify-center text-gray-400">Yüklənir...</div>
            ) : rooms.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-white p-16 text-center">
                    <div className="mb-4 text-5xl">🏠</div>
                    <p className="mb-1 font-medium text-gray-500">Hələ otaq əlavə edilməyib</p>
                    <p className="text-sm text-gray-400">Zal, VIP, terras, kabinet və s. əlavə edin</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {rooms.map((room, idx) => {
                        const isTakeaway = Number(room.is_takeaway || 0) === 1;
                        return (
                        <div
                            key={room.id}
                            draggable={!isTakeaway}
                            onDragStart={() => !isTakeaway && handleDragStart(room.id)}
                            onDragOver={handleDragOver}
                            onDrop={() => handleDrop(room.id)}
                            className={`rounded-xl border-2 bg-white p-5 transition-all ${!isTakeaway ? 'cursor-move' : ''} ${
                                isTakeaway
                                    ? 'border-orange-200 bg-orange-50/30'
                                    : !room.active ? 'border-gray-200 opacity-50' : 'border-primary-100 hover:border-primary-300 hover:shadow-md'
                            } ${dragId === room.id ? 'opacity-40' : ''}`}
                        >
                            <div className="mb-3 flex items-start justify-between">
                                <div
                                    className="flex h-12 w-12 items-center justify-center rounded-xl text-2xl"
                                    style={{ background: isTakeaway ? 'linear-gradient(135deg,#fff3e0,#ffe0b2)' : 'linear-gradient(135deg, #e0f4fb, #b3e5f5)' }}
                                >
                                    {isTakeaway ? '🥡' : ICONS[idx % ICONS.length]}
                                </div>
                                <span
                                    className={`rounded-full px-2 py-1 text-xs font-bold ${
                                        isTakeaway ? 'bg-orange-100 text-orange-700' : room.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                    }`}
                                >
                                    {isTakeaway ? 'Takeaway' : room.active ? 'Aktiv' : 'Passiv'}
                                </span>
                            </div>
                            <h3 className="mb-1 font-bold text-gray-800">
                                {room.name}
                                {fdRooms && Number(room.delivery_only || 0) === 1 && (
                                    <span className="ml-2 rounded-full bg-orange-100 px-2 py-0.5 align-middle text-[10px] font-bold text-orange-700">🛵 Delivery</span>
                                )}
                            </h3>
                            <p className="mb-1 text-xs text-gray-400">🪑 {room.table_count} masa · Sıra: {room.sort_order}</p>
                            {isTakeaway && (
                                <p className="mb-3 text-[11px] font-semibold text-orange-600">Servis haqqı bu otaqda Takeaway kimi göstərilir</p>
                            )}
                            {!isTakeaway && <div className="mb-4" />}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => openEdit(room)}
                                    className="flex-1 rounded-lg bg-gray-100 py-1.5 text-xs font-semibold text-gray-600 transition-all hover:bg-gray-200"
                                >
                                    Redaktə
                                </button>
                                {!isTakeaway && (
                                    <button
                                        onClick={() => handleToggle(room)}
                                        className="flex-1 rounded-lg bg-amber-50 py-1.5 text-xs font-semibold text-amber-600 transition-all hover:bg-amber-100"
                                    >
                                        {room.active ? 'Deaktiv' : 'Aktiv'}
                                    </button>
                                )}
                                {!isTakeaway && room.table_count === 0 && (
                                    <button
                                        onClick={() => setDeleteCandidate(room)}
                                        className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-500 transition-all hover:bg-red-100"
                                    >
                                        Sil
                                    </button>
                                )}
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                        <h3 className="mb-5 text-lg font-bold text-gray-800">{editRoom ? 'Otağı redaktə et' : 'Yeni otaq'}</h3>
                        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                            <div>
                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Ad</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="Məs: Zal 1, VIP, Terras, Kabinet"
                                    autoFocus
                                    className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none"
                                />
                            </div>
                            {/* Sıralama avtomatikdir (gizli) — frontdə göstərilmir */}

                            {/* Ofisiant girişi — default: hamısı görür (tək tik). Untick → siyahı açılır. */}
                            {waiters.length > 0 && (
                                <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-3">
                                    <div className="mb-2 text-sm font-bold text-violet-800">👤 Ofisiant girişi</div>
                                    {/* Master tik */}
                                    <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-white px-2.5 py-2 text-sm font-semibold">
                                        <input
                                            type="checkbox"
                                            checked={!restrictWaiters}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setRestrictWaiters(false);
                                                    setForm((f) => ({ ...f, hidden_waiter_ids: [] }));
                                                } else {
                                                    setRestrictWaiters(true);
                                                }
                                            }}
                                            className="h-4 w-4 rounded border-gray-300 text-violet-600"
                                        />
                                        <span className="text-gray-700">Bütün ofisiantlar görə bilir</span>
                                    </label>
                                    {restrictWaiters && (
                                        <>
                                            <p className="mb-2 mt-2 text-[11px] leading-snug text-violet-700/80">
                                                İşarəsini götürdüyün ofisiant bu zalı öz PIN/parolu ilə <b>görməyəcək</b>.
                                            </p>
                                            <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                                                {waiters.map((w) => {
                                                    const allowed = !form.hidden_waiter_ids.map(Number).includes(Number(w.id));
                                                    return (
                                                        <label key={w.id} className="flex cursor-pointer items-center gap-2 rounded-lg bg-white px-2.5 py-2 text-sm">
                                                            <input
                                                                type="checkbox"
                                                                checked={allowed}
                                                                onChange={(e) => setForm((f) => {
                                                                    const set = new Set(f.hidden_waiter_ids.map(Number));
                                                                    if (e.target.checked) set.delete(Number(w.id)); else set.add(Number(w.id));
                                                                    return { ...f, hidden_waiter_ids: [...set] };
                                                                })}
                                                                className="h-4 w-4 rounded border-gray-300 text-violet-600"
                                                            />
                                                            <span className={allowed ? 'text-gray-700' : 'text-gray-400 line-through'}>{w.name}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Doluluq faizinə hesablansın? Söndürülsə bu zalın BÜTÜN masaları xaric olur */}
                            <label className="flex items-start gap-2 rounded-xl border border-sky-100 bg-sky-50/40 p-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.count_occupancy !== false}
                                    onChange={(e) => setForm({ ...form, count_occupancy: e.target.checked })}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-sky-600"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-bold text-sky-800">📊 Doluluq faizinə hesablansın</div>
                                    <div className="mt-0.5 text-[11px] leading-snug text-sky-700/80">
                                        QR menyuda "restoran X% doludur" göstəricisi. Söndürülsə, bu zalın <b>bütün masaları</b> faizə heç cür daxil olmur (masanın öz tiki açıq olsa belə).
                                    </div>
                                </div>
                            </label>

                            {/* Yalnız Food delivery otağı: masalarında ödəniş yalnız delivery (nağd/POS/bölünmüş yox) */}
                            {fdRooms && editRoom && (
                                <label className="flex items-start gap-2 rounded-xl border border-orange-200 bg-orange-50/50 p-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!form.delivery_only}
                                        onChange={(e) => setForm({ ...form, delivery_only: e.target.checked })}
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-600"
                                    />
                                    <div className="flex-1">
                                        <div className="text-sm font-bold text-orange-800">🛵 Yalnız Food delivery</div>
                                        <div className="mt-0.5 text-[11px] leading-snug text-orange-700/80">
                                            Bu otağın masalarında hesab yalnız Food delivery ilə bağlanır. Nağd, POS və hesab bölmə görünmür.
                                        </div>
                                    </div>
                                </label>
                            )}
                            <div className="mt-2 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                >
                                    Ləğv et
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 rounded-xl py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                >
                                    {editRoom ? 'Yadda saxla' : 'Əlavə et'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {deleteCandidate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && setDeleteCandidate(null)}>
                    <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
                        <h3 className="mb-2 text-lg font-bold text-gray-800">Otağı sil</h3>
                        <p className="mb-5 text-sm text-gray-500">
                            <span className="font-semibold text-gray-700">{deleteCandidate.name}</span> otağını silmək istəyirsiniz?
                        </p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setDeleteCandidate(null)}
                                className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                            >
                                Ləğv et
                            </button>
                            <button
                                type="button"
                                onClick={handleDelete}
                                className="flex-1 rounded-xl bg-red-500 py-2.5 text-sm font-bold text-white hover:bg-red-600"
                            >
                                Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Rooms;
